import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, formatApiError, installmentValue } from "@/lib/api";
import Mascot from "@/components/Mascot";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Package, Star, Share2, Boxes } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (p) => (!p ? "" : p.startsWith("http") ? p : `${BACKEND}${p}`);

export default function LojaVirtual() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState({ parcelas: 12, juros_percent: 0 });

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/catalog"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); api.get("/catalog/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const parcela = (v) => money(installmentValue(v, cfg.parcelas, cfg.juros_percent));

  const share = (p) => {
    const specs = [p.category, p.brand].filter(Boolean).join(" · ");
    const foto = p.photos?.[0] ? imgUrl(p.photos[0]) : "";
    const msg = `*${p.name}*\n${specs}\n${money(p.price)} ou ${cfg.parcelas}x de ${parcela(p.price)} no cartão${foto ? "\n\n" + foto : ""}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="space-y-8" data-testid="loja-virtual-page">
      <div className="relative alpha-card overflow-hidden p-8 sm:p-12">
        <div className="absolute -right-10 -top-10 h-56 w-56 rounded-full bg-gold/10 blur-3xl" />
        <div className="relative flex items-center justify-between gap-6 flex-wrap">
          <div className="max-w-lg">
            <span className="text-xs px-3 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold-bright">Loja Alpha Prime</span>
            <h1 className="mt-3 text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-white">Loja Virtual</h1>
            <p className="text-zinc-400 mt-2">Produtos do Estoque marcados como "Loja Virtual" aparecem aqui e na vitrine pública.</p>
          </div>
          <Mascot size={110} className="hidden md:block" />
        </div>
        <Link to="/app/estoque" data-testid="loja-goto-estoque" className="inline-block mt-6">
          <Button className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>
            <Boxes size={16} className="mr-2" />Gerenciar produtos no Estoque
          </Button>
        </Link>
        <p className="text-xs text-zinc-500 mt-2">Para adicionar produto, fotos ou destaque, cadastre no Estoque e defina a disponibilidade como "PDV + Loja Virtual" ou "Apenas Loja Virtual".</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gold" /></div>
      ) : items.length === 0 ? (
        <div className="alpha-card p-12 text-center" data-testid="loja-empty">
          <Package className="mx-auto text-zinc-600" size={40} />
          <p className="text-zinc-400 mt-3">Nenhum produto marcado para a Loja Virtual ainda.</p>
          <p className="text-zinc-500 text-sm mt-1">Vá ao Estoque e marque um produto como "Loja Virtual".</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((p) => (
            <div key={p.id} data-testid={`loja-item-${p.id}`} className="alpha-card overflow-hidden group flex flex-col">
              <div className="aspect-square overflow-hidden bg-[#0A0A0C] relative">
                {p.photos?.[0] ? (
                  <img src={imgUrl(p.photos[0])} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-700"><Package size={44} /></div>
                )}
                {p.featured && <span data-testid={`loja-featured-${p.id}`} className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full bg-gold/90 text-black font-semibold flex items-center gap-1"><Star size={10} className="fill-black" />Destaque</span>}
                {p.quantity <= 0 && <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-black/70 text-red-300">Sem estoque</span>}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-semibold text-white leading-tight">{p.name}</h3>
                <p className="text-sm text-zinc-500 mt-1">{[p.category, p.brand].filter(Boolean).join(" · ")}</p>
                <div className="mt-3">
                  <span className="text-xl font-mono-kpi font-extrabold text-gold-bright">{money(p.price)}</span>
                  <p className="text-[11px] text-zinc-500 mt-0.5">ou {cfg.parcelas}x de {parcela(p.price)} no cartão <span className="opacity-70">(simulação)</span></p>
                </div>
                <Button data-testid={`loja-share-${p.id}`} onClick={() => share(p)} variant="outline" className="mt-auto pt-0 w-full border-green-500/30 bg-transparent text-green-300 hover:bg-green-500/10 h-9 mt-3"><Share2 size={14} className="mr-1.5" />Compartilhar no WhatsApp</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
